import React, { useState } from 'react';
import { useCollectBadgeMutation, useGenerateDigitalBadgeMutation } from '../../slices/badgeApi';
import { getLocalVerificationUrl } from '../../constants';
import LinkedInShareButton from './LinkedInShareButton';
import util from '../../styles/utilities.module.css';
import styles from './BadgeCard.module.css';

const getBadgeIcon = (badgeType) => {
  switch (badgeType) {
    case 'fluency':
      return '⭐';
    case 'engagement':
      return '⭐';
    case 'impact':
      return '⭐';
    default:
      return '⭐';
  }
};

const statusVariantClass = (badgeType) => ({
  fluency: styles.statusFluency,
  engagement: styles.statusEngagement,
  impact: styles.statusImpact
}[badgeType] || styles.statusDefault);

const BadgeCard = ({ badge, onViewDetails, onLinkedInShare }) => {
  const [collectBadge, { isLoading: isCollecting }] = useCollectBadgeMutation();
  const [generateDigitalBadge, { isLoading: isGenerating }] = useGenerateDigitalBadgeMutation();
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const isEarned = badge.is_earned;
  const digitalBadge = (isEarned && badge.credential_id) ? {
    badge_id: badge.badge_id,
    name: badge.name,
    credential_id: badge.credential_id,
    verification_url: badge.verification_url,
    issued_date: badge.earned_at,
    badge_type: badge.badge_type,
    description: badge.description,
    linkedin_shared_date: badge.linkedin_shared_date,
    view_count: badge.view_count
  } : null;
  
  const handleCollectBadge = async () => {
    if (!badge.is_eligible || isEarned) return;
    
    try {
      const result = await collectBadge({ 
        badgeId: badge.badge_id
      }).unwrap();
      
      // Backend returns complete badge data - no merging needed
      if (result.badge) {
        onViewDetails?.(result.badge);
      }
    } catch (error) {
      console.error('Badge collection failed:', error);
    }
  };

  const handleGenerateDigital = async () => {
    if (!isEarned) return;
    
    try {
      const result = await generateDigitalBadge({ 
        badgeId: badge.badge_id,
        forceRegenerate: !!digitalBadge 
      }).unwrap();
      
      if (result.success) {
        // Badge generation successful - parent component will refetch data
        console.log('Digital badge generated successfully');
      }
    } catch (error) {
      console.error('Failed to generate digital badge:', error);
    }
  };

  const handleCopyVerificationUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleLinkedInSharingSuccess = (result) => {
    onLinkedInShare?.(result);
  };

  const handleLinkedInSharingError = (error) => {
    console.error('LinkedIn sharing error:', error);
    // Could show a toast notification here
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const cardClassName = [
    styles.card,
    isEarned ? styles.cardEarned : styles.cardAvailable
  ].join(' ');

  const badgeStatusClassName = [
    styles.status,
    statusVariantClass(badge.badge_type)
  ].join(' ');

  const visibleRequirements = Array.isArray(badge.requirements)
    ? badge.requirements.filter((requirement) => !requirement.hidden)
    : [];

  const primaryGradientButton = `${util.btn} ${util['btn-sm']} ${util['btn--primaryGradient']}`;
  const accentGradientButton = `${util.btn} ${util['btn-sm']} ${util['btn--accentGradient']}`;
  const ghostButton = `${util.btn} ${util['btn-sm']} ${util['btn--ghost']}`;
  const successButton = `${util.btn} ${util['btn-sm']} ${util['btn--successGradient']}`;
  const neutralButton = `${util.btn} ${util['btn-sm']} ${util['btn--neutral']}`;

  return (
    <div className={cardClassName}>
      <div className={styles.header}>
        <div className={styles.icon}>
          {getBadgeIcon(badge.badge_type)}
        </div>
        <div className={styles.titleWrap}>
          <h3 className={styles.title}>{badge.name}</h3>
          <span className={badgeStatusClassName}>
            {badge.badge_type.charAt(0).toUpperCase() + badge.badge_type.slice(1)}
          </span>
        </div>
      </div>

      <div className={styles.content}>
        <p className={styles.description}>{badge.description}</p>

        {isEarned && digitalBadge && (
          <div className={styles.digitalInfo}>
            <div className={styles.digitalRow}>
              <strong>Earned:</strong> {formatDate(digitalBadge.issued_date)}
            </div>

            {digitalBadge.linkedin_shared_date && (
              <div className={styles.digitalRow}>
                <strong>Shared to LinkedIn:</strong> {formatDate(digitalBadge.linkedin_shared_date)}
              </div>
            )}

            <div className={styles.meta}>
              <span>👁️ {digitalBadge.view_count || 0} views</span>
            </div>
          </div>
        )}

        {!isEarned && !badge.is_eligible && visibleRequirements.length > 0 && (
          <div className={styles.requirements}>
            <h4 className={styles.requirementsTitle}>Requirements:</h4>
            <ul className={styles.requirementsList}>
              {visibleRequirements.map((requirement, index) => (
                <li key={index}>{requirement}</li>
              ))}
            </ul>
          </div>
        )}

        {!isEarned && badge.is_eligible && (
          <div className={styles.eligible}>
            <span className={styles.eligibleText}>✅ Ready to earn this badge!</span>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        {isEarned && digitalBadge && (
          <>
            <button
              className={ghostButton}
              onClick={() => onViewDetails?.(digitalBadge)}
            >
              View Details
            </button>

            <button
              className={ghostButton}
              onClick={() => handleCopyVerificationUrl(getLocalVerificationUrl(digitalBadge.verification_url))}
              disabled={showCopySuccess}
            >
              {showCopySuccess ? '✅ Copied!' : '📋 Copy Link'}
            </button>

            <LinkedInShareButton
              badge={badge}
              digitalBadge={digitalBadge}
              onSharingSuccess={handleLinkedInSharingSuccess}
              onSharingError={handleLinkedInSharingError}
              className={accentGradientButton}
              variant="primary"
            />
          </>
        )}

        {isEarned && !digitalBadge && (
          <button
            className={successButton}
            onClick={handleGenerateDigital}
            disabled={isGenerating}
          >
            {isGenerating ? '⏳ Generating...' : '🎖️ Generate Digital Badge'}
          </button>
        )}

        {!isEarned && badge.is_eligible && (
          <button
            className={primaryGradientButton}
            onClick={handleCollectBadge}
            disabled={isCollecting}
          >
            {isCollecting ? '⏳ Collecting...' : '🎖️ Collect Badge'}
          </button>
        )}

        {!isEarned && !badge.is_eligible && (
          <button
            className={neutralButton}
            disabled
          >
            Complete Requirements
          </button>
        )}
      </div>
    </div>
  );
};

export default BadgeCard;
